import { NgModule, inject } from "@angular/core";
import { CanActivateFn, RouterModule, Routes } from "@angular/router";
import { AuthGuardService } from "./services/security";
import { HomeComponent } from "./modules/home/components/home/home.component";
import { TwoFactorSendCodeComponent } from "@modules/account/login/components/twofactor/send-code/send-code.component";
import { TwoFactorVerifyCodeComponent } from "@modules/account/login/components/twofactor/verify-code/verify-code.component";
import { NewPasswordComponent } from "@modules/account/login/components/newpassword/newpassword.component";
import { ConfirmAccountComponent } from "@modules/account/login/components/confirm-account/confirm-account.component";
import { SendTokenResetPasswordComponent } from "@modules/account/login/components/sendtokennewpassword/send-token-possword.component";
import { LoginComponent } from "./modules/account/login/components/login/login.component";
import { LoginAsExternalComponent } from "./modules/account/login/components/login-as-external/login-as-external.component";

// Parité V1 : AuthGuardService reste une classe (même logique, même redirection login?returnUrl) ;
// Angular 21 attend des CanActivateFn dans les routes → wrapper fonctionnel sans changement de comportement.
// canActivate() ne prend aucun argument en V1 (il lit window.location.href directement) — reproduit à l'identique.
const authGuard: CanActivateFn = () => inject(AuthGuardService).canActivate();

const appRoutes: Routes = [
  {
    path: "",
    data: { breadcrumb: "Accueil" },
    canActivate: [authGuard],
    children: [
      { path: "", redirectTo: "home", pathMatch: "full" },
      {
        path: "home",
        component: HomeComponent,
        data: { breadcrumb: null, breadcrumbVisible: false },
        canActivate: [authGuard],
      },
      {
        path: "dossiers-permanents",
        data: { breadcrumb: "Dossiers permanents" },
        loadChildren: () =>
          import("./modules/dossiers-permanents/dossiers-permanents.module").then((m) => m.DossiersPermanentsModule),
      },
      {
        path: "dmlm",
        data: { breadcrumb: "Déclarer ma location meublé" },
        loadChildren: () => import("./modules/dmlm/dmlm.module").then((m) => m.DMLMModule),
      },
      {
        path: "dmlm/error",
        data: { breadcrumb: "Déclarer ma location meublé" },
        loadChildren: () => import("./modules/dmlm/dmlm.module").then((m) => m.DMLMModule),
      },
      {
        path: "client-requests",
        data: { breadcrumb: "Suivi demandes" },
        loadChildren: () =>
          import("./modules/client-requests/client-requests.module").then((m) => m.ClientRequestsModule),
      },
      // Parité V1 : bloc "holidays" (legacy) commenté dans ClientApp — conservé commenté à l'identique.
      // {
      //   path: "holidays",
      //   data: { breadcrumb: "Gestion des congés" },
      //   loadChildren: () =>
      //     import("./modules/holidays/holidays.module").then((m) => m.HolidaysModule),
      // },
      {
        path: "holidays-1",
        data: { breadcrumb: "Gestion des congés" },
        loadChildren: () =>
          import("./modules/holidays-1/holidays1.module").then((m) => m.Holidays1Module),
      },
      {
        path: "coopting",
        data: { breadcrumb: "Parrainage" },
        loadChildren: () => import("./modules/coopting/coopting.module").then((m) => m.CooptingModule),
      },
      {
        path: "my-account",
        data: { breadcrumb: "Mon compte" },
        loadChildren: () =>
          import("./modules/account/edit/account-infos.module").then((m) => m.EditMyInfosModule),
      },
      {
        path: "my-connectors",
        data: { breadcrumb: "Mes connecteurs" },
        loadChildren: () =>
          import("./modules/application-connector/application-connector.module").then((m) => m.ApplicationConnectorModule),
      },
      {
        path: "cra",
        data: { breadcrumb: "Compte rendu d'activité" },
        loadChildren: () => import("./modules/portage/cra-1/cra.module").then((m) => m.Cra1Module),
      },
      {
        path: "cra-1",
        data: { breadcrumb: "Compte rendu d'activité" },
        loadChildren: () => import("./modules/portage/cra-1/cra.module").then((m) => m.Cra1Module),
      },
      // Parité V1 : bloc "expense-note" (legacy) commenté dans ClientApp — conservé commenté.
      {
        path: "expense-note-1",
        data: { breadcrumb: "Opérations comptables et notes de frais" },
        loadChildren: () =>
          import("./modules/expense-note-1/expense-note1.module").then((m) => m.ExpenseNote1Module),
      },
      {
        path: "budget-insight",
        data: { breadcrumb: "Agrégation de comptes" },
        loadChildren: () =>
          import("./modules/budget-insight/budget-insight.module").then((m) => m.BudgetInsightModule),
      },
      {
        path: "help",
        data: { breadcrumb: "Manuel d'utilisation" },
        loadChildren: () => import("./modules/help/help.module").then((m) => m.HelpModule),
      },
      {
        path: "document",
        data: { breadcrumb: "Guides et fiches conseils" },
        loadChildren: () => import("./modules/document/document.module").then((m) => m.DocumentModule),
      },
      // Parité V1 : blocs "simulateur" / "simulateur-portage" (legacy) commentés dans ClientApp — conservés commentés.
      {
        path: "simulateur-1",
        data: { breadcrumb: "Simulateurs" },
        loadChildren: () =>
          import("./modules/simulateur-1/simulateur-home-1.module").then((m) => m.SimulateurHomeModule1),
      },
      {
        path: "simulateur-portage-1",
        data: { breadcrumb: "Simulation paye consultant" },
        loadChildren: () =>
          import("./modules/simulateur-1/portage-salarial/simulateur-portage-salarial-1.module").then((m) => m.SimulateurPortageSalarialModule1),
      },
      // Parité V1 : bloc "gestion-document" (legacy) commenté dans ClientApp — conservé commenté.
      {
        path: "gestion-document-1",
        data: { breadcrumb: "Mes documents" },
        loadChildren: () =>
          import("./modules/gestion-document-1/gestion-document1.module").then((m) => m.GestionDocument1Module),
      },
      {
        path: "visa-fiscal",
        data: { breadcrumb: "Visa fiscal" },
        loadChildren: () =>
          import("./modules/visa-fiscal/home-visafiscal.module").then((m) => m.HomeVisaFiscalModule),
      },
      {
        path: "visa-fiscal-ecf",
        data: { breadcrumb: "Visa fiscal & Ecf" },
        loadChildren: () =>
          import("./modules/menu-ecf-visafiscal/menu-ecf-visafiscal.module").then((m) => m.HomeEcfVisaFiscalModule),
      },
      {
        path: "ecf",
        data: { breadcrumb: "Ecf" },
        loadChildren: () => import("./modules/ecf/home-ecf.module").then((m) => m.HomeEcfModule),
      },
      {
        path: "e-invoice",
        data: { breadcrumb: "Facturation client" },
        loadChildren: () => import("./modules/e-invoice/e-invoice.module").then((m) => m.EInvoiceModule),
      },
      {
        path: "compte-resultat",
        data: { breadcrumb: "Compte de resultat" },
        loadChildren: () =>
          import("./modules/compte-resultat/compte-resultat.module").then((m) => m.CompteResultatModule),
      },
      {
        path: "professionnels-IT",
        data: { breadcrumb: "Professionnels IT" },
        loadChildren: () =>
          import("./modules/professionnels-IT/professionnels-IT.module").then((m) => m.ProfessionnelsITModule),
      },
      // Parité V1 : route "simulateur-societe" (ancien module) encore déclarée dans ClientApp mais
      // inatteignable par la navigation (arbitrage T012 : écrans legacy non migrés, route non reprise).
      {
        path: "simulateur-societe-1",
        data: { breadcrumb: "Simulation société" },
        loadChildren: () =>
          import("./modules/simulateur-societe-1/simulateur-societe-1.module").then((m) => m.SimulateurSocieteModule1),
      },
      {
        path: "sales-register",
        data: { breadcrumb: "Suivi des facturations clients" },
        loadChildren: () =>
          import("./modules/sales-register/sales-register.module").then((m) => m.SalesRegisterModule),
      },
      {
        path: "invoice",
        data: { breadcrumb: null },
        loadChildren: () =>
          import("./modules/invoice-home/invoice-home.module").then((m) => m.InvoiceHomeModule),
      },
      {
        path: "electronic-signature",
        data: { breadcrumb: null },
        loadChildren: () =>
          import("./modules/sign-document-yousign/sign-document-yousign.module").then((m) => m.SignDocumentYousignModule),
      },
      {
        path: "banque",
        data: { breadcrumb: "Mes opérations bancaires" },
        loadChildren: () => import("./modules/bank/bank.module").then((m) => m.BankModule),
      },
      // Parité V1 : route "portee-tracking" (legacy) non reprise — module mort (arbitrage T012).
      {
        path: "portee-tracking-1",
        data: { breadcrumb: "Décompte d'activités" },
        loadChildren: () =>
          import("./modules/portage-activity-tracking-1/portage-activity-tracking.module").then((m) => m.PortageActivityTrackingModule1),
      },
      {
        path: "gestionnaires",
        data: { breadcrumb: "Gestion des gestionnaires" },
        loadChildren: () =>
          import("./modules/gestionnaire/gestionnaire.module").then((m) => m.GestionnaireModule),
      },
    ],
  },
  { path: "login", component: LoginComponent },
  { path: "loginAsExternal", component: LoginAsExternalComponent },
  { path: "mon-espace/loginAsExternal", redirectTo: "loginAsExternal" },
  {
    path: "login/send-code",
    data: { breadcrumb: null, breadcrumbVisible: false },
    component: TwoFactorSendCodeComponent,
  },
  {
    path: "login/verify-code",
    data: { breadcrumb: null, breadcrumbVisible: false },
    component: TwoFactorVerifyCodeComponent,
  },
  { path: "account/confirmEmail", component: ConfirmAccountComponent },
  { path: "account/ResetPassword", component: NewPasswordComponent },
  { path: "lostPassword", component: SendTokenResetPasswordComponent },
  {
    path: "gestionnaires/activate",
    loadChildren: () => import("./modules/gestionnaire-public/gestionnaire-public.module").then(m => m.GestionnairePublicModule)
  },
];

@NgModule({
  imports: [RouterModule.forRoot(appRoutes, { useHash: false })],
  exports: [RouterModule],
})
export class AppRoutingModule {}
